import LayoutContext from 'components/Layout/LayoutContext';
import { AssemblyModalitiesNumber } from 'helpers/enums/Assembleia';
import moment from 'moment';
import {
  ButtonThemes,
  CheckCircleIcon,
  ClockIcon,
  Col,
  DatePicker,
  IconButton,
  Input,
  Modal,
  ModalSizes,
  Row,
  TimesCircleIcon,
  ToastTypes,
} from 'plataforma-fundacao-componentes';

import { AssembleiaStatus } from 'helpers/enums/AssembleiaStatus';
import { differenceInObj } from 'helpers/utils/ArrayUtils';
import {
  formatData,
  formatHora,
  isNullOrUndefined,
} from 'helpers/utils/HTMLUtils';
import { TextRange } from 'helpers/utils/StringUtils';
import translation from 'locales/translation';
import { ModalProps } from 'plataforma-fundacao-componentes/dist/components/modal/Modal';
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  assembleiasAssessorEdit,
  assembleiasAssessorLoadEvento,
} from 'store/modules/AssembleiasAssessorActions';
import {
  AssembleiaAssessorDadosInformativos,
  IAssembleiasAssessorEvent,
} from 'store/modules/AssembleiasAssessorReducer';
import InfoAssemblyValidator from './validation/InfoAssemblyValidator';

interface IProps extends ModalProps {
  modo?: AssemblyModalitiesNumber;
  evento: IAssembleiasAssessorEvent;
}

interface Inputs {
  title: string;
  description: string;
  url: string;
  initialDateOnline: string;
  initialHourOnline: string;
  endDateOnline: string;
  endHourOnline: string;
  initialDatePresential: string;
  initialHourPresential: string;
  endDatePresential: string;
  endHourPresential: string;
}

const infoAssemblyPrefix = translation.Assessor.Modal.InfoAssembly;

export const InfoAssembly = (props: IProps) => {
  const [sent, setSent] = useState<boolean>(false);
  const [loadingButton, setLoadingButton] = useState<boolean>(false);

  const dispatch = useDispatch();
  const { showToast } = useContext(LayoutContext);

  const handleClose = useCallback(() => {
    props.onClose && props.onClose(props.modalKey);
  }, [props]);

  const initialValues: AssembleiaAssessorDadosInformativos = useMemo(() => {
    const fullDateInicioEvento = moment(props.evento.dataEvento);
    const fullDateFimEvento = moment(props.evento.dataFimEvento);
    const hasOnline = props.modo !== AssemblyModalitiesNumber.ONLINE;
    const fullDateInicioEventoOnline = hasOnline
      ? moment(props.evento.dataEventoOnline)
      : undefined;
    const fullDateFimEventoOnline = hasOnline
      ? moment(props.evento.dataFimEventoOnline)
      : undefined;
    return {
      idEvento: props.evento.id,
      nome: TextRange(props.evento.nome, 200),
      descricao: props.evento.descricao ?? '',
      urlTransmissao: props.evento.urlTransmissao || '',
      dataEvento: fullDateInicioEvento.format('DD/MM/YYYY'),
      horarioEvento: fullDateInicioEvento.format('HH:mm'),
      dataFimEvento: fullDateFimEvento.format('DD/MM/YYYY'),
      horarioFimEvento: fullDateFimEvento.format('HH:mm'),
      dataEventoOnline: fullDateInicioEventoOnline
        ? fullDateInicioEventoOnline.format('DD/MM/YYYY')
        : undefined,
      horarioEventoOnline: fullDateInicioEventoOnline
        ? fullDateInicioEventoOnline.format('HH:mm')
        : undefined,
      dataFimEventoOnline: fullDateFimEventoOnline
        ? fullDateFimEventoOnline.format('DD/MM/YYYY')
        : undefined,
      horarioFimEventoOnline: fullDateFimEventoOnline
        ? fullDateFimEventoOnline.format('HH:mm')
        : undefined,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [inputs, setInputs] = useReducer(
    (prev: Inputs, next: Partial<Inputs>) => {
      return { ...prev, ...next };
    },
    {
      title: '',
      description: '',
      url: '',
      initialDateOnline: '',
      initialHourOnline: '',
      endDateOnline: '',
      endHourOnline: '',
      initialDatePresential: '',
      initialHourPresential: '',
      endDatePresential: '',
      endHourPresential: '',
    }
  );

  const compare = differenceInObj(
    initialValues,
    {
      dataEvento: inputs.initialDatePresential,
      dataEventoOnline: inputs.initialDateOnline,
      dataFimEvento: inputs.endDatePresential,
      dataFimEventoOnline: inputs.endDateOnline,
      descricao: isNullOrUndefined(inputs.description)
        ? ''
        : inputs.description,
      horarioEvento: inputs.initialHourPresential,
      horarioEventoOnline: inputs.initialHourOnline,
      horarioFimEvento: inputs.endHourPresential,
      horarioFimEventoOnline: inputs.endHourOnline,
      nome: inputs.title,
      urlTransmissao: inputs.url,
    },
    ['idEvento']
  );

  const validator = InfoAssemblyValidator(inputs, compare, props.modo!);

  const handleSubmit = useCallback(
    (values: AssembleiaAssessorDadosInformativos) => {
      if (!sent) setSent(true);
      if (!validator || !validator.allIsValid) return;

      setLoadingButton(true);

      dispatch(
        assembleiasAssessorEdit(
          values,
          () => {
            setLoadingButton(false);
            handleClose();
            dispatch(assembleiasAssessorLoadEvento(values.idEvento, () => {}));
          },
          (error) => {
            setLoadingButton(false);
            showToast({
              pauseOnFocusLoss: true,
              theme: ToastTypes.Error,
              showStatusBar: true,
              timeout: 4000,
              label:
                translation.Toasts.Errors.Keys[
                  `${
                    error.response?.data.message ||
                    error.response?.data ||
                    error.message
                  }` as keyof typeof translation.Toasts.Errors.Keys
                ] ?? translation.Toasts.Errors.defaultAlternative,
            });
          }
        )
      );
    },
    [dispatch, handleClose, sent, showToast, validator]
  );

  const isFinished = useMemo(() => {
    if (
      [
        AssembleiaStatus.Encerrado,
        AssembleiaStatus.EncerradoSemQuorum,
      ].includes(props.evento.status)
    ) {
      return true;
    }
    return false;
  }, [props.evento.status]);

  const primaryFields = useCallback(() => {
    return (
      <Fragment>
        <Col cols={[12, 6, 6, 6, 6]}>
          <Input
            data-testid={'input-initial-date-presential'}
            id={'input-initial-date-presential'}
            value={inputs.initialDatePresential ?? ''}
            onChange={(e) => {
              setInputs({
                ...inputs,
                initialDatePresential: formatData(
                  e.target.value.split('-').join('/')
                ),
              });
            }}
            rightObject={
              <DatePicker
                onChange={(date) => {
                  setInputs({
                    ...inputs,
                    initialDatePresential: date,
                  });
                }}
                queryToAppend={'#input-initial-date-presential'}
                initialValue={inputs.initialDatePresential}
                closeOnChange
                disabled={isFinished || loadingButton}
              />
            }
            label={infoAssemblyPrefix.dataEventoAssembleia}
            placeholder={
              infoAssemblyPrefix.dataEncerramentoEventoAssembleiaPlaceholder
            }
            disabled={isFinished || loadingButton}
            error={
              sent && validator && validator['initialDatePresential']?.error
            }
            helperText={
              sent && validator
                ? validator['initialDatePresential']?.helperText
                : ''
            }
          />
        </Col>
        <Col cols={[12, 6, 6, 6, 6]}>
          <Input
            id={'input-initial-hour-presential'}
            data-testid={'input-initial-hour-presential'}
            value={inputs.initialHourPresential ?? ''}
            onChange={(e) => {
              setInputs({
                ...inputs,
                initialHourPresential: formatHora(e.target.value),
              });
            }}
            rightObject={
              <IconButton
                style={{ pointerEvents: 'none' }}
                icon={<ClockIcon />}
              />
            }
            label={infoAssemblyPrefix.horaEventoAssembleia}
            placeholder={infoAssemblyPrefix.horaEventoAssembleiaPlaceholder}
            disabled={isFinished || loadingButton}
            error={
              sent && validator && validator['initialHourPresential']?.error
            }
            helperText={
              sent && validator
                ? validator['initialHourPresential']?.helperText
                : ''
            }
          />
        </Col>
        <Col cols={[12, 6, 6, 6, 6]}>
          <Input
            data-testid={'input-end-date-presential'}
            id={'input-end-date-presential'}
            value={inputs.endDatePresential ?? ''}
            onChange={(e) => {
              setInputs({
                ...inputs,
                endDatePresential: formatData(
                  e.target.value.split('-').join('/')
                ),
              });
            }}
            rightObject={
              <DatePicker
                onChange={(date) => {
                  setInputs({
                    ...inputs,
                    endDatePresential: date,
                  });
                }}
                queryToAppend={'#input-end-date-presential'}
                initialValue={inputs.endDatePresential}
                disabled={isFinished || loadingButton}
                closeOnChange
              />
            }
            label={infoAssemblyPrefix.dataEncerramentoEventoAssembleia}
            placeholder={
              infoAssemblyPrefix.dataEncerramentoEventoAssembleiaPlaceholder
            }
            disabled={isFinished || loadingButton}
            error={sent && validator && validator['endDatePresential']?.error}
            helperText={
              sent && validator
                ? validator['endDatePresential']?.helperText
                : ''
            }
          />
        </Col>
        <Col cols={[12, 6, 6, 6, 6]}>
          <Input
            id={'input-end-hour-presential'}
            data-testid={'input-end-hour-presential'}
            value={inputs.endHourPresential ?? ''}
            onChange={(e) => {
              setInputs({
                ...inputs,
                endHourPresential: formatHora(e.target.value),
              });
            }}
            rightObject={
              <IconButton
                style={{ pointerEvents: 'none' }}
                icon={<ClockIcon />}
              />
            }
            label={infoAssemblyPrefix.horaEncerramentoEventoAssembleia}
            placeholder={
              infoAssemblyPrefix.horaEncerramentoEventoAssembleiaPlaceholder
            }
            disabled={isFinished || loadingButton}
            error={sent && validator && validator['endHourPresential']?.error}
            helperText={
              sent && validator
                ? validator['endHourPresential']?.helperText
                : ''
            }
          />
        </Col>
      </Fragment>
    );
  }, [inputs, isFinished, loadingButton, sent, validator]);

  const extraFields = useCallback(() => {
    const flgUseFieldsOnline = props.modo !== AssemblyModalitiesNumber.ONLINE;

    const config = flgUseFieldsOnline
      ? {
          initialDate: inputs.initialDateOnline,
          setInitialDate: (date: string) =>
            setInputs({ initialDateOnline: date }),
          initialDateValidator: validator?.initialDateOnline,
          initialHour: inputs.initialHourOnline,
          setInitialHour: (hour: string) =>
            setInputs({ initialHourOnline: hour }),
          initialHourValidator: validator?.initialHourOnline,
          endDate: inputs.endDateOnline,
          setEndDate: (date: string) => setInputs({ endDateOnline: date }),
          endDateValidator: validator?.endDateOnline,
          endHour: inputs.endHourOnline,
          setEndHour: (hour: string) => setInputs({ endHourOnline: hour }),
          endHourValidator: validator?.endHourOnline,
        }
      : {
          initialDate: inputs.initialDatePresential,
          setInitialDate: (date: string) =>
            setInputs({ initialDatePresential: date }),
          initialDateValidator: validator?.initialDatePresential,
          initialHour: inputs.initialHourPresential,
          setInitialHour: (hour: string) =>
            setInputs({ initialHourPresential: hour }),
          initialHourValidator: validator?.initialHourPresential,
          endDate: inputs.endDatePresential,
          setEndDate: (date: string) => setInputs({ endDatePresential: date }),
          endDateValidator: validator?.endDatePresential,
          endHour: inputs.endHourPresential,
          setEndHour: (hour: string) => setInputs({ endHourPresential: hour }),
          endHourValidator: validator?.endHourPresential,
        };

    return (
      <Fragment>
        <Col cols={[12, 6, 6, 6, 6]}>
          <Input
            data-testid={'input-initial-date'}
            id={'input-initial-date'}
            value={config.initialDate}
            onChange={(e) =>
              config.setInitialDate(
                formatData(e.target.value.split('-').join('/'))
              )
            }
            rightObject={
              <DatePicker
                onChange={(date) => config.setInitialDate(date)}
                queryToAppend={'#input-initial-date'}
                initialValue={config.initialDate}
                disabled={isFinished || loadingButton}
                closeOnChange
              />
            }
            label={infoAssemblyPrefix.dataEventoAssembleia}
            placeholder={
              infoAssemblyPrefix.dataEncerramentoEventoAssembleiaPlaceholder
            }
            disabled={isFinished || loadingButton}
            error={sent && validator && config.initialDateValidator?.error}
            helperText={
              sent && validator ? config.initialDateValidator?.helperText : ''
            }
          />
        </Col>
        <Col cols={[12, 6, 6, 6, 6]}>
          <Input
            id={'input-initial-hour'}
            data-testid={'input-initial-hour'}
            value={config.initialHour}
            onChange={(e) => config.setInitialHour(formatHora(e.target.value))}
            rightObject={
              <IconButton
                style={{ pointerEvents: 'none' }}
                icon={<ClockIcon />}
              />
            }
            label={infoAssemblyPrefix.horaEventoAssembleia}
            placeholder={infoAssemblyPrefix.horaEventoAssembleiaPlaceholder}
            disabled={isFinished || loadingButton}
            error={sent && validator && config.initialHourValidator?.error}
            helperText={
              sent && validator ? config.initialHourValidator?.helperText : ''
            }
          />
        </Col>
        <Col cols={[12, 6, 6, 6, 6]}>
          <Input
            data-testid={'input-end-date'}
            id={'input-end-date'}
            value={config.endDate}
            onChange={(e) =>
              config.setEndDate(formatData(e.target.value.split('-').join('/')))
            }
            rightObject={
              <DatePicker
                onChange={(date) => config.setEndDate(date)}
                queryToAppend={'#input-end-date'}
                initialValue={config.endDate}
                disabled={isFinished || loadingButton}
                closeOnChange
              />
            }
            label={infoAssemblyPrefix.dataEncerramentoEventoAssembleia}
            placeholder={
              infoAssemblyPrefix.dataEncerramentoEventoAssembleiaPlaceholder
            }
            disabled={isFinished || loadingButton}
            error={sent && validator && config.endDateValidator?.error}
            helperText={
              sent && validator ? config.endDateValidator?.helperText : ''
            }
          />
        </Col>
        <Col cols={[12, 6, 6, 6, 6]}>
          <Input
            id={'input-end-hour'}
            data-testid={'input-end-hour'}
            value={config.endHour}
            onChange={(e) => config.setEndHour(formatHora(e.target.value))}
            rightObject={
              <IconButton
                style={{ pointerEvents: 'none' }}
                icon={<ClockIcon />}
              />
            }
            label={infoAssemblyPrefix.horaEncerramentoEventoAssembleia}
            placeholder={
              infoAssemblyPrefix.horaEncerramentoEventoAssembleiaPlaceholder
            }
            disabled={isFinished || loadingButton}
            error={sent && validator && config.endHourValidator?.error}
            helperText={
              sent && validator ? config.endHourValidator?.helperText : ''
            }
          />
        </Col>
      </Fragment>
    );
  }, [
    inputs.endDateOnline,
    inputs.endDatePresential,
    inputs.endHourOnline,
    inputs.endHourPresential,
    inputs.initialDateOnline,
    inputs.initialDatePresential,
    inputs.initialHourOnline,
    inputs.initialHourPresential,
    isFinished,
    loadingButton,
    props.modo,
    sent,
    validator,
  ]);

  const orderFields = useCallback(() => {
    switch (props.evento.modo) {
      case AssemblyModalitiesNumber.HIBRIDO_PRESENCIAL_DIGITAL:
        return (
          <Fragment>
            <Row>
              <Col>
                <p>{infoAssemblyPrefix.eventoPresencialTitle}</p>
              </Col>
              {primaryFields()}
            </Row>
            <Row>
              <Col>
                <p>{infoAssemblyPrefix.eventoDigitalTitle}</p>
              </Col>
              {extraFields()}
            </Row>
          </Fragment>
        );
      case AssemblyModalitiesNumber.HIBRIDO_DIGITAL_AGENCIA:
        return (
          <Fragment>
            <Row>
              <Col>
                <p>{infoAssemblyPrefix.eventoDigitalTitle}</p>
              </Col>
              {primaryFields()}
            </Row>
            <Row>
              <Col>
                <p>{infoAssemblyPrefix.eventoAgenciaTitle}</p>
              </Col>
              {extraFields()}
            </Row>
          </Fragment>
        );
      case AssemblyModalitiesNumber.ONLINE:
        return (
          <Row>
            <Col>
              <p>{infoAssemblyPrefix.eventoDigitalTitle}</p>
            </Col>
            {extraFields()}
          </Row>
        );
      case AssemblyModalitiesNumber.HIBRIDO_SIMULTANEO:
        return (
          <Fragment>
            <Row>
              <Col>
                <p>{infoAssemblyPrefix.eventoDigitalTitle}</p>
              </Col>
              {primaryFields()}
            </Row>
            <Row>
              <Col>
                <p>{infoAssemblyPrefix.eventoSimultaneoTitle}</p>
              </Col>
              {extraFields()}
            </Row>
          </Fragment>
        );
      default:
        return null;
    }
  }, [extraFields, primaryFields, props.evento.modo]);

  useEffect(() => {
    if (initialValues) {
      setInputs({
        title: initialValues.nome,
        description: initialValues.descricao,
        url: initialValues.urlTransmissao,
        initialDateOnline: initialValues.dataEventoOnline,
        initialHourOnline: initialValues.horarioEventoOnline,
        endDateOnline: initialValues.dataFimEventoOnline,
        endHourOnline: initialValues.horarioFimEventoOnline,
        initialDatePresential: initialValues.dataEvento,
        initialHourPresential: initialValues.horarioEvento,
        endDatePresential: initialValues.dataFimEvento,
        endHourPresential: initialValues.horarioFimEvento,
      });
    }
  }, [initialValues]);

  return (
    <Modal
      title={infoAssemblyPrefix.title}
      preventEscExit={props.preventEscExit || false}
      preventMaskExit={props.preventMaskExit || false}
      modalKey={props.modalKey}
      mobileOn={props.mobileOn}
      onClose={props.onClose}
      size={ModalSizes.Large}
      footer={
        !isFinished
          ? [
              {
                theme: ButtonThemes.Secondary,
                leftIcon: <TimesCircleIcon />,
                children: infoAssemblyPrefix.buttons.cancelButtonLabel,
                onClick: () => props.onClose!(props.modalKey),
              },
              {
                theme: ButtonThemes.Primary,
                leftIcon: <CheckCircleIcon />,
                children: infoAssemblyPrefix.buttons.confirmButtonLabel,
                loading: loadingButton,
                disabled:
                  isFinished ||
                  loadingButton ||
                  (sent && !!validator && !validator.allIsValid) ||
                  Object.keys(compare).length === 0,
                onClick: () =>
                  handleSubmit({
                    idEvento: props.evento.id,
                    nome: inputs.title,
                    descricao: inputs.description,
                    urlTransmissao: inputs.url,
                    dataEvento: inputs.initialDatePresential,
                    dataEventoOnline: inputs.initialDateOnline,
                    horarioEvento: inputs.initialHourPresential,
                    horarioEventoOnline: inputs.initialHourOnline,
                    dataFimEvento: inputs.endDatePresential,
                    dataFimEventoOnline: inputs.endDateOnline,
                    horarioFimEvento: inputs.endHourPresential,
                    horarioFimEventoOnline: inputs.endHourOnline,
                  }),
              },
            ]
          : []
      }
    >
      <Row>
        <Col>
          <Input
            label={infoAssemblyPrefix.fields.nome.label}
            placeholder={infoAssemblyPrefix.fields.nome.placeholder}
            value={inputs.title ?? ''}
            onChange={(e) => setInputs({ ...inputs, title: e.target.value })}
            counter
            maxLength={200}
            disabled={isFinished || loadingButton}
            error={sent && validator && validator['title']?.error}
            helperText={sent && validator ? validator['title']?.helperText : ''}
          />
        </Col>
        <Col>
          <Input
            label={infoAssemblyPrefix.fields.descricao.label}
            placeholder={infoAssemblyPrefix.fields.descricao.placeholder}
            value={inputs.description ?? ''}
            onChange={(e) =>
              setInputs({ ...inputs, description: e.target.value })
            }
            disabled={isFinished || loadingButton}
          />
        </Col>
        <Col>
          <Input
            label={infoAssemblyPrefix.fields.urlTransmissao.label}
            placeholder={infoAssemblyPrefix.fields.urlTransmissao.placeholder}
            value={inputs.url ?? ''}
            onChange={(e) => setInputs({ ...inputs, url: e.target.value })}
            disabled={isFinished || loadingButton}
            error={sent && validator && validator['url']?.error}
            helperText={sent && validator ? validator['url']?.helperText : ''}
          />
        </Col>
      </Row>
      {orderFields()}
    </Modal>
  );
};
